import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import Confetti from "react-confetti";

const QuizPage = () => {
  const [questionData, setQuestionData] = useState(null);
  const [userAnswer, setUserAnswer] = useState("");
  const [feedback, setFeedback] = useState("");
  const [score, setScore] = useState({
    correct: 0,
    wrong: 0,
    skipped: 0,
    totalPoints: 0,
  });
  const [showConfetti, setShowConfetti] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const capitalize = (str) =>
    str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();

  const fetchQuestion = useCallback(async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get("https://langbridge-backend.onrender.com/api/quiz/question", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = res.data;

      // If data is empty or no quiz, set questionData to null
      if (!data || Object.keys(data).length === 0) {
        setQuestionData(null);
      } else {
        // Modify fill-in-the-blank question text if needed
        if (data.type === "fill-in-the-blank" && data.targetLang) {
          data.question = `Translate this: "${
            data.question.match(/"(.+?)"/)?.[1] || data.question
          }" into "${capitalize(data.targetLang)}"`;
        }
        setQuestionData(data);
      }

      setUserAnswer("");
      setFeedback("");
    } catch (err) {
      console.error("Failed to fetch question", err);
      setQuestionData(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchQuestion();
  }, [fetchQuestion]);

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!userAnswer || isSubmitting) return;

    const token = localStorage.getItem("token");
    setIsSubmitting(true);

    try {
      const res = await axios.post(
        "https://langbridge-backend.onrender.com/api/quiz",
        {
          question: questionData.question,
          options: questionData.options || [],
          correctAnswer: questionData.correctAnswer,
          userAnswer,
          type: questionData.type === "multiple-choice" ? "mcq" : "fill",
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const { isCorrect, score: quizScore } = res.data;

      setFeedback(isCorrect ? "✅ Correct!" : "❌ Incorrect.");
      setShowConfetti(isCorrect);

      setScore((prev) => ({
        ...prev,
        correct: prev.correct + (isCorrect ? 1 : 0),
        wrong: prev.wrong + (!isCorrect ? 1 : 0),
        totalPoints: prev.totalPoints + quizScore,
      }));

      setTimeout(() => {
        setShowConfetti(false);
        setIsSubmitting(false);
        fetchQuestion();
      }, 2000);
    } catch (err) {
      console.error("Error submitting answer", err);
      setIsSubmitting(false);
    }
  };

  const handleSkip = (e) => {
    if (e) e.preventDefault();
    if (isSubmitting) return;
    setScore((prev) => ({ ...prev, skipped: prev.skipped + 1 }));
    fetchQuestion();
  };

  const accuracy =
    score.correct + score.wrong > 0
      ? Math.round((score.correct / (score.correct + score.wrong)) * 100)
      : 0;

  // Render loading  while fetching
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray p-6 text-center font-semibold">
        Loading quiz...
      </div>
    );
  }

  // Render if no quizzes are available
  if (!questionData) {
    return (
      <div className="p-6 bg-gray min-h-screen text-green text-center font-semibold">
        No quizzes available at the moment. Please check back later!
      </div>
    );
  }

  return (
    <section className="bg-gray">
      {showConfetti && <Confetti />}

      <div className="max-w-2xl mx-auto p-6">
        <h2 className="text-center text-2xl font-bold mb-4 text-blue">Quiz</h2>

        <p className="text-lg mb-4 font-medium">{questionData.question}</p>

        {questionData.type === "multiple-choice" ? (
          <div className="space-y-2">
            {questionData.options.map((option, idx) => (
              <button
                key={idx}
                disabled={isSubmitting}
                className={`w-full border rounded p-2 text-left transition duration-200 ${
                  userAnswer === option
                    ? "bg-blue text-white"
                    : "hover:bg-purple"
                } ${isSubmitting ? "opacity-50 cursor-not-allowed" : ""}`}
                onClick={() => setUserAnswer(option)}
              >
                {option}
              </button>
            ))}
          </div>
        ) : (
          <input
            type="text"
            placeholder="Type your answer"
            value={userAnswer}
            onChange={(e) => setUserAnswer(e.target.value)}
            className="border rounded px-4 py-2 w-full mb-4 focus:outline-none focus:ring focus:border-blue"
            disabled={isSubmitting}
          />
        )}

        {feedback && (
          <p
            className={`mt-4 font-semibold transition duration-300 ${
              feedback.includes("Correct") ? "text-green" : "text-red-600"
            }`}
          >
            {feedback}
          </p>
        )}

        <div className="flex gap-4 mt-6">
          <button
            type="button"
            onClick={(e) => handleSubmit(e)}
            disabled={isSubmitting}
            className={`bg-blue text-gray hover:bg-purple px-4 py-2 rounded ${
              isSubmitting ? "opacity-50 cursor-not-allowed" : ""
            }`}
          >
            Submit
          </button>
          <button
            type="button"
            onClick={(e) => handleSkip(e)}
            disabled={isSubmitting}
            className="bg-purple text-gray hover:bg-blue px-4 py-2 rounded"
          >
            Skip
          </button>
        </div>

        <div className="mt-6 text-sm text-gray-700 space-y-1">
          <div>
            ✅ Correct: {score.correct} | ❌ Wrong: {score.wrong} | ⏭ Skipped:{" "}
            {score.skipped}
          </div>
          <div>🌟 Points: {score.totalPoints}</div>
          <div>🎯 Accuracy: {accuracy}%</div>
        </div>
      </div>
    </section>
  );
};

export default QuizPage;
